import type { ReactElement, ReactNode } from 'react';

import { parse } from 'date-fns';

/**
 * Convert a kebab-case string to camelCase.
 * @param value
 * @returns string
 */
export function kebabToCamelCase(value: string) {
    return value.replace(/-([a-z])/g, g => {
        return g[1]?.toUpperCase() ?? '';
    });
}

/**
 * Convert to a string to PascalCase
 * @param str
 * @returns string
 */
export function toPascalCase(str: string) {
    return str
        .replace(/[^a-zA-Z0-9]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
}

/**
 * Convert to a string to camelCase
 * @param str
 * @returns string
 */
export function toCamelCase(str: string) {
    return str
        .replace(/[^a-zA-Z0-9]/g, ' ')
        .split(' ')
        .map((word, index) =>
            index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join('');
}

export function formatNumber(
    amount?: number,
    locale: Intl.LocalesArgument = 'da-DK',
    options: Intl.NumberFormatOptions = {},
): string {
    if (amount === undefined) {
        return '';
    }

    const defaultOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: 0,
        signDisplay: 'auto',
    };

    const formatter = new Intl.NumberFormat(locale, {
        ...defaultOptions,
        ...options,
    });

    return formatter.format(amount);
}

const defaultFormatCurrencyOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'DKK',
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
};

export function formatCurrency(
    amount?: number,
    locale: Intl.LocalesArgument = 'da-DK',
    options: Intl.NumberFormatOptions = {},
): string {
    if (amount === undefined) {
        return '';
    }

    const formatter = new Intl.NumberFormat(locale, {
        ...defaultFormatCurrencyOptions,
        ...options,
    });
    return formatter.format(amount);
}

export function formatCurrencyTemplate(
    amount?: number,
    locale: Intl.LocalesArgument = 'da-DK',
    options: Intl.NumberFormatOptions = {},
): {
    template: string;
    amount: string;
    currency: string;
} {
    if (amount === undefined) {
        return { template: '', amount: '', currency: '' };
    }
    const formattedAmount = formatCurrency(amount, locale, { ...defaultFormatCurrencyOptions, ...options });
    const amountValue = formatNumber(amount, locale);
    const currencyValue = formattedAmount.replace(amountValue, '').trim();
    const priceTemplate = formattedAmount.replace(amountValue, '{amount}').replace(currencyValue, '{currency}');

    return {
        template: priceTemplate,
        amount: amountValue,
        currency: currencyValue,
    };
}

export function formatDate(
    date?: Date | string,
    locale: Intl.LocalesArgument = 'da-DK',
    options: Intl.DateTimeFormatOptions = {},
): string {
    const dateToFormat = typeof date !== 'string' ? date : new Date(date);

    if (!dateToFormat || isNaN(dateToFormat.getTime())) {
        return '';
    }

    const defaultOptions: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    };

    const formatter = new Intl.DateTimeFormat(locale, {
        ...defaultOptions,
        ...options,
    });

    return formatter.format(dateToFormat);
}

export function parseDate(date: string): Date {
    return parse(date, 'dd.MM.yyyy', new Date());
}

export function parseDashedDate(date: string): Date {
    return parse(date, 'yyyy-MM-dd', new Date());
}

export type InterpolateValues = { [key: string]: string | number | ReactNode };

export type InterpolateOutput<T extends InterpolateValues> = T extends { [key: string]: string | number }
    ? string
    : ReactElement;

/**
 * Interpolate a string with values
 * @param template - The template string with placeholders
 * @param values - The object containing values to replace the placeholders
 * @param keyFormat - The format of the keys in the template (default is curly braces)
 * @returns The interpolated string
 */
export const interpolateString = <T extends InterpolateValues = { [key: string]: string }>(
    template: string,
    values?: T,
    keyFormat: { start: string; end: string } = { start: '{', end: '}' },
): InterpolateOutput<T> => {
    const { start, end } = keyFormat;
    const regex = new RegExp(`${start}(.*?)${end}`, 'g');

    // if no interpolate params provided
    if (!values) {
        return template as InterpolateOutput<T>;
    }

    // if values has any ReactNodes
    const hasNode = Object.values(values).some(val => typeof val !== 'string' && typeof val !== 'number');

    if (hasNode) {
        const parts = template.split(regex).filter(Boolean);

        const labels = parts.map(p => {
            if (values[p]) {
                return values[p];
            }
            return p;
        });

        return (
            <span>
                {labels.map((l, index) => (
                    <span key={String(l) + index}>{l}</span>
                ))}
            </span>
        ) as InterpolateOutput<T>;
    }

    // if values contains only strings
    return template.replace(regex, (_, key) => (String(values[key]) as string) || '') as InterpolateOutput<T>;
};

interface FontSizeOptions {
    minFontSize?: number;
    maxFontSize?: number;
    minLineHeight?: number;
    maxLineHeight?: number;
}

interface DynamicFontSizeInput {
    text: string;
    minContainerWidthPx: number;
    maxContainerWidthPx: number;
    options?: FontSizeOptions;
}

interface DynamicFontSizeOutput {
    fontSize: string;
    lineHeight: string;
}

/**
 * Calculates dynamic font size and line height based on container width and text length.
 * @param text - The text to be styled
 * @param minContainerWidthPx - The minimum width of the container in px
 * @param maxContainerWidthPx - The maximum width of the container in px
 * @param options - Options for font size and line height customization
 * @returns An object containing the font size and line height in px
 */
export function getDynamicFontSize({
    text,
    minContainerWidthPx,
    maxContainerWidthPx,
    options = {},
}: DynamicFontSizeInput): DynamicFontSizeOutput {
    const { minFontSize = 16, maxFontSize = 160, minLineHeight = 16, maxLineHeight = 32 } = options;

    const containerWidthPx = (minContainerWidthPx + maxContainerWidthPx) / 2;
    const estimatedTextWidth = text.length * 0.6;

    let fontSize = containerWidthPx / estimatedTextWidth;
    fontSize = Math.max(minFontSize, Math.min(maxFontSize, fontSize));

    const lineHeight =
        ((fontSize - minFontSize) * (maxLineHeight - minLineHeight)) / (maxFontSize - minFontSize) + minLineHeight;

    return { fontSize: `${fontSize}px`, lineHeight: `${lineHeight}px` };
}

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
